// pages/TeamManagement.tsx
// Accessible to clinic admins and platform admins.
// Manage staff members, their permissions, and passwords.

import { useEffect, useState } from "react";
import {
  Plus,
  RotateCcw,
  Eye,
  EyeOff,
  Loader2,
  UserX,
  UserCheck,
  Copy,
  Check,
} from "lucide-react";
import { getAuthToken } from "../lib/supabase";
import { PERMISSIONS, type Permission } from "../lib/permissions";

const API_URL = import.meta.env.VITE_API_URL as string;

const PERMISSION_LABELS: Record<Permission, string> = {
  view_all_conversations: "View all conversations",
  send_messages: "Send messages",
  assign_conversations: "Assign conversations",
  manage_knowledge: "Manage knowledge",
  manage_templates: "Manage templates",
  manage_reminders: "Manage reminders",
  view_dashboard: "View dashboard",
  manage_billing: "Manage billing",
  manage_clinic_settings: "Access settings",
  manage_bot: "Manage bot",
  view_patients: "View patients",
};

interface StaffMember {
  staff_id: string;
  name: string;
  role: string | null;
  is_clinic_admin: boolean;
  is_active: boolean;
  permissions: Record<Permission, boolean>;
}

export default function TeamManagement({ embedded = false }: { embedded?: boolean }) {
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null); // staff_id being saved
  const [error, setError] = useState<string | null>(null);

  // Add member form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Temp password display
  const [tempPasswords, setTempPasswords] = useState<Record<string, string>>({});
  const [resetting, setResetting] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  async function fetchMembers() {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/clinic/members`, {
        headers: token ? { Authorization: token } : {},
      });
      if (!res.ok) throw new Error("Failed to load members");
      const json = await res.json();
      setMembers(json.members as StaffMember[]);
    } catch {
      setError("Unable to load team members. Please refresh.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchMembers();
  }, []);

  async function togglePermission(staffId: string, permission: Permission, value: boolean) {
    const member = members.find((m) => m.staff_id === staffId);
    if (!member) return;

    const updated = {
      ...member.permissions,
      [permission]: value,
    };

    setMembers((prev) =>
      prev.map((m) =>
        m.staff_id === staffId ? { ...m, permissions: updated } : m
      )
    );

    setSaving(staffId);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/clinic/members/${staffId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
        body: JSON.stringify({ permissions: updated }),
      });
      if (!res.ok) throw new Error("Failed to save");
    } catch {
      // Revert on error
      setMembers((prev) =>
        prev.map((m) =>
          m.staff_id === staffId ? { ...m, permissions: member.permissions } : m
        )
      );
      setError("Failed to save permissions. Please try again.");
    } finally {
      setSaving(null);
    }
  }

  async function toggleActive(staffId: string, isActive: boolean) {
    setSaving(staffId);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/clinic/members/${staffId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
        body: JSON.stringify({ is_active: isActive }),
      });
      if (!res.ok) throw new Error("Failed to update");
      setMembers((prev) =>
        prev.map((m) =>
          m.staff_id === staffId ? { ...m, is_active: isActive } : m
        )
      );
    } catch {
      setError("Failed to update member status. Please try again.");
    } finally {
      setSaving(null);
    }
  }

  async function resetPassword(staffId: string) {
    setResetting(staffId);
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${API_URL}/clinic/staff/generate-temp-password/${staffId}`,
        {
          method: "POST",
          headers: token ? { Authorization: token } : {},
        }
      );
      if (!res.ok) throw new Error("Failed to reset");
      const { temp_password } = await res.json();
      setTempPasswords((prev) => ({ ...prev, [staffId]: temp_password }));
    } catch {
      setError("Failed to reset password. Please try again.");
    } finally {
      setResetting(null);
    }
  }

  async function copyToClipboard(staffId: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(staffId);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Fallback — silently ignore
    }
  }

  async function handleAddMember(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !newPassword.trim()) return;
    setAdding(true);
    setAddError(null);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/clinic/members`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
        body: JSON.stringify({
          full_name: newName.trim(),
          role: newRole.trim() || null,
          password: newPassword,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to add member");
      }
      setNewName("");
      setNewRole("");
      setNewPassword("");
      setShowAddForm(false);
      await fetchMembers();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Failed to add member.");
    } finally {
      setAdding(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 text-sm gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading team…
      </div>
    );
  }

  const activeMembers = members.filter((m) => m.is_active);
  const inactiveMembers = members.filter((m) => !m.is_active);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        {!embedded ? (
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Team Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage staff members and their access permissions.
            </p>
          </div>
        ) : (
          <div />
        )}
        <button
          onClick={() => setShowAddForm((v) => !v)}
          className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add member
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-3 underline text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Add member form */}
      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">New team member</h2>
          <form onSubmit={handleAddMember} className="flex flex-col gap-3 max-w-md">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">
                Full name *
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Dr. Sarah Lee"
                required
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">
                Role / title
              </label>
              <input
                type="text"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                placeholder="e.g. Receptionist"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">
                Initial password *
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Set a temporary password"
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showNewPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            {addError && (
              <p className="text-sm text-red-500">{addError}</p>
            )}
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={adding}
                className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {adding && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {adding ? "Adding…" : "Add member"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setAddError(null);
                }}
                className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Permissions grid */}
      {activeMembers.length === 0 ? (
        <p className="text-sm text-gray-500">No active team members yet.</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600 min-w-[180px]">
                    Member
                  </th>
                  {PERMISSIONS.map((p) => (
                    <th
                      key={p}
                      className="px-3 py-3 font-medium text-gray-600 text-center min-w-[80px]"
                      title={PERMISSION_LABELS[p]}
                    >
                      <span className="block text-[11px] leading-tight max-w-[70px] mx-auto">
                        {PERMISSION_LABELS[p]}
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 font-medium text-gray-600 text-center min-w-[100px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {activeMembers.map((member) => (
                  <tr
                    key={member.staff_id}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{member.name}</div>
                      {member.role && (
                        <div className="text-xs text-gray-400">{member.role}</div>
                      )}
                      {member.is_clinic_admin && (
                        <div className="text-xs text-blue-600 font-medium mt-0.5">
                          Clinic admin
                        </div>
                      )}
                      {saving === member.staff_id && (
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Saving…
                        </div>
                      )}
                    </td>
                    {PERMISSIONS.map((p) => (
                      <td key={p} className="px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={
                            member.is_clinic_admin || !!member.permissions[p]
                          }
                          disabled={member.is_clinic_admin || saving === member.staff_id}
                          onChange={(e) =>
                            togglePermission(member.staff_id, p, e.target.checked)
                          }
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                          title={
                            member.is_clinic_admin
                              ? "Clinic admins have all permissions"
                              : PERMISSION_LABELS[p]
                          }
                        />
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-center gap-2">
                        {/* Reset password */}
                        <button
                          onClick={() => resetPassword(member.staff_id)}
                          disabled={resetting === member.staff_id}
                          title="Reset password"
                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50"
                        >
                          {resetting === member.staff_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3.5 h-3.5" />
                          )}
                          Reset pwd
                        </button>
                        {/* Show temp password */}
                        {tempPasswords[member.staff_id] && (
                          <div className="flex items-center gap-1 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                            <span className="text-xs font-mono text-yellow-800">
                              {tempPasswords[member.staff_id]}
                            </span>
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  member.staff_id,
                                  tempPasswords[member.staff_id]
                                )
                              }
                              title="Copy"
                              className="text-yellow-600 hover:text-yellow-800"
                            >
                              {copied === member.staff_id ? (
                                <Check className="w-3.5 h-3.5" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        )}
                        {/* Deactivate */}
                        <button
                          onClick={() => toggleActive(member.staff_id, false)}
                          disabled={saving === member.staff_id}
                          title="Deactivate member"
                          className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
                        >
                          <UserX className="w-3.5 h-3.5" />
                          Deactivate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inactive members */}
      {inactiveMembers.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 mb-3">
            Inactive members ({inactiveMembers.length})
          </h2>
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm divide-y divide-gray-100">
            {inactiveMembers.map((member) => (
              <div
                key={member.staff_id}
                className="flex items-center justify-between px-4 py-3"
              >
                <div>
                  <span className="text-sm text-gray-500 line-through">{member.name}</span>
                  {member.role && (
                    <span className="text-xs text-gray-400 ml-2">{member.role}</span>
                  )}
                </div>
                <button
                  onClick={() => toggleActive(member.staff_id, true)}
                  disabled={saving === member.staff_id}
                  className="flex items-center gap-1.5 text-xs text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                >
                  {saving === member.staff_id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <UserCheck className="w-3.5 h-3.5" />
                  )}
                  Reactivate
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
